import { ChainData, ChainType } from "@0xsquid/squid-types";
import { BrowserProvider, JsonRpcSigner } from "ethers";
import {
  defineChain,
  type Account,
  type Chain,
  type Client,
  type Transport,
} from "viem";
import { Config, createConfig, http } from "wagmi";
import {
  coinbaseWallet,
  injected,
  metaMask,
  safe,
  walletConnect,
} from "wagmi/connectors";
import { HederaExtensionData } from "../services/internal/hederaService";
import { hederaWalletConnect } from "./connectors/evm/hederaWalletConnect";
import { SQUID_METADATA, WALLETCONNECT_PROJECT_ID } from "./constants";

export const createWagmiConfig = (
  squidChains: ChainData[],
  hederaExtensions: HederaExtensionData[]
): Config => {
  const filteredEvmChains = squidChains.filter(
    (chain) => chain.chainType === ChainType.EVM
  );

  if (filteredEvmChains.length === 0) {
    throw new Error("At least one chain is required");
  }

  const wagmiChains: Chain[] = filteredEvmChains.map((chain) => {
    return defineChain({
      id: Number(chain.chainId),
      name: chain.networkName,
      nativeCurrency: {
        name: chain.nativeCurrency.name,
        symbol: chain.nativeCurrency.symbol,
        decimals: chain.nativeCurrency.decimals,
      },
      rpcUrls: {
        public: {
          http: [chain.rpc],
        },
        default: {
          http: [chain.rpc],
        },
      },
    });
  });

  const wcMetadata = {
    url: SQUID_METADATA.url,
    name: SQUID_METADATA.name,
    icons: [SQUID_METADATA.icon],
    description: SQUID_METADATA.description,
  };

  return createConfig({
    ssr: true,
    chains: wagmiChains as [Chain, ...Chain[]],
    transports: Object.fromEntries(
      wagmiChains.map((chain) => [
        chain.id,
        http(chain.rpcUrls.public.http[0] ?? ""),
      ])
    ),

    connectors: [
      injected(),
      safe({
        allowedDomains: [/app.safe.global$/],
      }),
      metaMask({
        dappMetadata: {
          name: SQUID_METADATA.name,
          url: SQUID_METADATA.url,
          iconUrl: SQUID_METADATA.icon,
        },
      }),
      coinbaseWallet({
        appName: SQUID_METADATA.name,
        appLogoUrl: SQUID_METADATA.icon,
      }),
      walletConnect({
        projectId: WALLETCONNECT_PROJECT_ID,
        metadata: wcMetadata,
      }),
      ...hederaExtensions.map((extension) =>
        hederaWalletConnect({
          projectId: WALLETCONNECT_PROJECT_ID,
          metadata: wcMetadata,
          extension,
        })
      ),
    ],
  });
};

// Taken from wagmi docs
// https://wagmi.sh/react/guides/ethers
export function clientToSigner(client: Client<Transport, Chain, Account>) {
  const { account, chain, transport } = client;
  if (!account || !chain || !transport) {
    return undefined;
  }
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  };
  const provider = new BrowserProvider(transport, network);
  const signer = new JsonRpcSigner(provider, account.address);
  return signer;
}
